import { useState, useEffect } from 'react';

import { RiveDumpster } from '@/components/RiveDumpster';

interface ChestOpeningModalProps {
  isOpen: boolean;
  chestCount: number;
  processingStep: 'signing' | 'sending' | 'processing' | 'confirming';
  attemptCount?: number;
  maxAttempts?: number;
  batchNumber?: number;
  totalBatches?: number;
  isEmbeddedWallet?: boolean;
}

export function ChestOpeningModal({
  isOpen,
  chestCount,
  processingStep,
  attemptCount = 0,
  maxAttempts = 20,
  batchNumber = 0,
  totalBatches = 1,
  isEmbeddedWallet = false
}: ChestOpeningModalProps) {
  const [dots, setDots] = useState('');

  // Animate dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Add shimmer animation styles
  useEffect(() => {
    if (!isOpen) return;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getStepMessage = () => {
    switch (processingStep) {
      case 'signing':
        // Embedded wallets don't show a signing popup
        return isEmbeddedWallet
          ? 'Preparing transaction...'
          : 'Please sign the transaction in your wallet';
      case 'sending':
        return 'Sending transaction to blockchain';
      case 'processing':
        return 'Processing your chest opening';
      case 'confirming':
        return 'Confirming rewards on-chain';
      default:
        return 'Processing';
    }
  };

  const getStepIcon = () => {
    switch (processingStep) {
      case 'signing':
        return isEmbeddedWallet ? '⏳' : '✍️';
      case 'sending':
        return '📡';
      case 'processing':
        return '⚙️';
      case 'confirming':
        return '🎰';
      default:
        return '⏳';
    }
  };

  // Calculate overall progress percentage considering all steps and batches
  const getOverallProgress = () => {
    const stepsWeight = {
      'signing': 10,
      'sending': 20,
      'processing': 30,
      'confirming': 40
    };

    const baseProgress = stepsWeight[processingStep] || 0;
    const confirmingProgress = processingStep === 'confirming' ? (attemptCount / maxAttempts) * 40 : 0;
    const batchProgress = totalBatches > 1 ? ((batchNumber - 1) / totalBatches) * 100 : 0;

    const currentBatchProgress = baseProgress + confirmingProgress;
    const totalProgress = batchProgress + (currentBatchProgress / totalBatches);

    return Math.min(Math.round(totalProgress), 99); // Never show 100% until actually complete
  };

  const progressPercentage = getOverallProgress();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
      <div
        className="relative p-8 rounded-2xl max-w-md w-full mx-4"
        style={{
          backgroundColor: 'rgba(40, 40, 40, 0.95)',
          border: '2px solid #50E4F9',
          boxShadow: '0 0 30px rgba(80, 228, 249, 0.3)',
        }}
      >
        {/* Animated Dumpster */}
        <div className="flex justify-center mb-6">
          <RiveDumpster
            width={120}
            height={120}
            isOpen={true}
          />
        </div>

        {/* Opening Message */}
        <h2
          className="text-3xl font-bold text-center mb-2"
          style={{
            fontFamily: 'Outfit, sans-serif',
            color: '#50E4F9',
            textShadow: '0 0 20px rgba(80, 228, 249, 0.5)',
          }}
        >
          OPENING {chestCount} CHEST{chestCount > 1 ? 'S' : ''}
        </h2>

        {/* Batch Progress for multiple transactions */}
        {totalBatches > 1 && batchNumber > 0 && (
          <p
            className="text-center text-sm mb-2"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            Processing batch {batchNumber} of {totalBatches}
          </p>
        )}

        {/* Step Icon and Message */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="text-3xl">{getStepIcon()}</span>
          <p
            className="text-lg text-center"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: '#FFFFFF',
            }}
          >
            {getStepMessage()}{dots}
          </p>
        </div>

        {/* Overall Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500, color: 'rgba(255, 255, 255, 0.8)' }}>
            <span>Overall Progress</span>
            <span style={{ fontWeight: 600, color: '#50E4F9' }}>{progressPercentage}%</span>
          </div>
          <div
            className="h-3 rounded-full overflow-hidden relative"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <div
              className="h-full transition-all duration-500 relative"
              style={{
                width: `${progressPercentage}%`,
                background: 'linear-gradient(90deg, #50E4F9, #00CED1)',
              }}
            >
              {/* Animated glow effect on progress bar */}
              <div className="absolute inset-0 opacity-50" style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'shimmer 2s infinite'
              }} />
            </div>
          </div>
          {totalBatches > 1 && (
            <div className="text-xs mt-1" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.5)' }}>
              Transaction {batchNumber} of {totalBatches}
            </div>
          )}
        </div>

        {/* Embedded Wallet Info Banner */}
        {isEmbeddedWallet && (
          <div
            className="mb-4 p-3 rounded-lg text-sm"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'rgba(80, 228, 249, 0.1)',
              border: '1px solid rgba(80, 228, 249, 0.3)',
              color: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <div className="flex items-start gap-2">
              <span>ℹ️</span>
              <span>
                Using embedded wallet. Transaction will process automatically — no signing popup will appear.
              </span>
            </div>
          </div>
        )}

        {/* Processing Steps */}
        <div className="space-y-3 mb-6">
          <StepIndicator
            step="1"
            label={isEmbeddedWallet ? "Prepare Transaction" : "Sign Transaction"}
            status={processingStep === 'signing' ? 'active' : processingStep !== 'signing' ? 'complete' : 'pending'}
          />
          <StepIndicator
            step="2"
            label="Submit to Blockchain"
            status={processingStep === 'sending' ? 'active' : ['processing', 'confirming'].includes(processingStep) ? 'complete' : 'pending'}
          />
          <StepIndicator
            step="3"
            label="Process Opening"
            status={processingStep === 'processing' ? 'active' : processingStep === 'confirming' ? 'complete' : 'pending'}
          />
          <StepIndicator
            step="4"
            label="Confirm Rewards"
            status={processingStep === 'confirming' ? 'active' : 'pending'}
          />
        </div>

        {/* Fun Messages */}
        <div className="text-center text-sm" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.5)' }}>
          {processingStep === 'confirming' && attemptCount > 5 && (
            <p>🦝 Trash Bandits are counting your rewards...</p>
          )}
          {processingStep === 'confirming' && attemptCount > 10 && (
            <p>Almost there! The blockchain is busy today...</p>
          )}
          {processingStep === 'confirming' && attemptCount > 15 && (
            <p>Thanks for your patience! Finalizing rewards...</p>
          )}
        </div>

        {/* Warning message for slow confirmations */}
        {attemptCount > 10 && (
          <div
            className="mt-4 p-3 rounded-lg text-sm"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'rgba(251, 139, 0, 0.1)',
              border: '1px solid rgba(251, 139, 0, 0.3)',
              color: '#FB8B00',
            }}
          >
            ⚠️ The blockchain is taking longer than usual. Your transaction is safe and will complete soon.
            {isEmbeddedWallet && (
              <div className="mt-2 text-xs">
                Embedded wallets may experience longer confirmation times. Your points will be credited once the transaction is fully confirmed.
              </div>
            )}
            {totalBatches > 1 && (
              <div className="mt-2 text-xs">
                Opening many chests may require multiple transactions. If some transactions fail due to network delays, you'll still receive the rewards from successful openings.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StepIndicator({
  step,
  label,
  status
}: {
  step: string;
  label: string;
  status: 'pending' | 'active' | 'complete';
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
          status === 'complete' ? 'bg-green-500 text-white' :
          status === 'active' ? 'bg-blue-500 text-white animate-pulse' :
          'bg-gray-600 text-gray-400'
        }`}
        style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600 }}
      >
        {status === 'complete' ? '✓' : step}
      </div>
      <span
        className={`flex-1 ${
          status === 'active' ? 'text-white font-semibold' :
          status === 'complete' ? 'text-green-400' :
          'text-gray-500'
        }`}
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {label}
      </span>
      {status === 'active' && (
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      )}
    </div>
  );
}